import { mnemonicToSeedSync } from "@scure/bip39";
import { HDKey } from "@scure/bip32";
import * as btc from "@scure/btc-signer";
import { bytesToHex } from "@noble/hashes/utils";
import type { ScriptType } from "./paths";

/**
 * Derives the seed from a mnemonic + optional BIP-39 passphrase. This seed
 * (and the mnemonic/passphrase that produced it) must never be sent
 * anywhere — everything downstream of this call stays in memory only.
 */
export function seedFromMnemonic(mnemonic: string, passphrase = ""): Uint8Array {
  return mnemonicToSeedSync(mnemonic.trim().toLowerCase(), passphrase);
}

export interface DerivedAddress {
  path: string;
  scriptType: ScriptType;
  address: string;
  publicKeyHex: string;
}

/** Derives the public key at a given BIP-32 path from a seed. */
export function derivePublicKeyAtPath(seed: Uint8Array, path: string): Uint8Array {
  const root = HDKey.fromMasterSeed(seed);
  const child = root.derive(path);
  if (!child.publicKey) {
    throw new Error(`Could not derive a public key at path ${path}`);
  }
  return child.publicKey;
}

/** Encodes a public key as an address of the given script type, mainnet. */
export function encodeAddress(publicKey: Uint8Array, scriptType: ScriptType): string {
  switch (scriptType) {
    case "p2pkh":
      return btc.p2pkh(publicKey, btc.NETWORK).address!;
    case "p2wpkh":
      return btc.p2wpkh(publicKey, btc.NETWORK).address!;
    case "p2sh-p2wpkh":
      // Nested SegWit (P2SH-wrapped P2WPKH): standard BIP-49 construction.
      // Correctness against the official BIP-49 test vector is checked in
      // derive.test.ts. Runtime verification required — see VERIFICATION.md.
      return btc.p2sh(btc.p2wpkh(publicKey, btc.NETWORK), btc.NETWORK).address!;
    case "p2tr": {
      // Taproot addresses commit to an x-only (32-byte) internal public key,
      // not the full 33-byte compressed key the other script types use —
      // drop the leading parity byte. @scure/btc-signer's p2tr() applies
      // the standard BIP-341 key-path-only tweak internally. Correctness
      // against the official BIP-86 test vector is checked in
      // derive.test.ts. Runtime verification required — see VERIFICATION.md.
      const xOnlyPubKey = publicKey.length === 33 ? publicKey.slice(1) : publicKey;
      return btc.p2tr(xOnlyPubKey, undefined, btc.NETWORK).address!;
    }
    default:
      throw new Error(`Unsupported script type: ${scriptType}`);
  }
}

/**
 * Derives a watch-only address from an extended PUBLIC key. No private key
 * material is ever involved — this is safe to run on a server, unlike
 * everything else in this package's derive functions, which assume a seed
 * and must stay client-side.
 *
 * ONLY STANDARD "xpub..." KEYS ARE SUPPORTED. Some wallets export "ypub..."
 * or "zpub..." instead, as an informal convention (SLIP-132, not part of
 * BIP-32 itself) to hint that a key is intended for BIP-49 or BIP-84 use.
 * The underlying key material in a ypub/zpub is byte-identical to the
 * xpub encoding of the same key — only the base58check version-byte
 * prefix differs. This function does not parse or special-case those
 * version bytes, and @scure/bip32's `HDKey.fromExtendedKey` has not been
 * confirmed (by actually running it — this environment cannot) to accept
 * non-standard version bytes at all; it may throw on them, or it may
 * decode them in a way this code hasn't verified produces a correct
 * result. Rather than guess, this function validates the "xpub" prefix
 * up front and refuses anything else with a clear error, so a wallet
 * that only exports zpub/ypub needs to be converted to standard xpub
 * format first (the key data is identical; many wallets and open-source
 * tools can re-encode the version bytes without needing the private key).
 */
export function deriveWatchOnlyAddress(
  xpub: string,
  change: 0 | 1,
  index: number,
  scriptType: ScriptType
): string {
  if (!xpub.startsWith("xpub")) {
    throw new Error(
      `deriveWatchOnlyAddress only supports standard "xpub..." keys. Got a key starting with ` +
        `"${xpub.slice(0, 4)}" instead. If this is a ypub or zpub, convert it to xpub format first — ` +
        `the underlying key is identical, only the version-byte prefix differs.`
    );
  }
  // Runtime verification required: confirm HDKey.fromExtendedKey/
  // deriveChild produce the address your own wallet software shows for
  // the same xpub/path (see VERIFICATION.md and the round-trip test in
  // __tests__/derive.test.ts, which checks this against a seed-derived
  // address rather than a hand-typed xpub string).
  const node = HDKey.fromExtendedKey(xpub);
  const child = node.deriveChild(change).deriveChild(index);
  if (!child.publicKey) {
    throw new Error(`Could not derive a public key at ${change}/${index} from the supplied xpub`);
  }
  return encodeAddress(child.publicKey, scriptType);
}

export function deriveAddress(seed: Uint8Array, path: string, scriptType: ScriptType): DerivedAddress {
  const publicKey = derivePublicKeyAtPath(seed, path);
  return {
    path,
    scriptType,
    address: encodeAddress(publicKey, scriptType),
    publicKeyHex: bytesToHex(publicKey),
  };
}
